// Проигрывание длинного фрагментированного MP4 без скачивания всего файла.
//
// Зачем вообще: наши записи льются на бакет во время съёмки, значит это
// фрагментированный MP4 без оглавления в начале. Обычный <video src> в таком
// случае строит оглавление сам — вычитывает ВЕСЬ файл (704 МБ, 544 фрагмента)
// и только потом показывает картинку. Замерено headless-Chrome 12.08:
// 1088 range-запросов за 25 секунд и ни одного кадра.
//
// Здесь оглавление приходит с сервера (GET /capture/:id/index, он читает mfra
// из хвоста объекта), а мы через MediaSource подкачиваем ровно те фрагменты,
// которые нужны для текущей позиции. Старт — секунда, перемотка — мгновенная,
// трафик — только за просмотренное.
import type { VistaCaptureIndexRes } from '@weern/contracts';

/** Сколько секунд держать загруженными ВПЕРЁД от текущей позиции. */
const AHEAD_SECONDS = 25;
/** И сколько оставлять позади, прежде чем выкидывать из буфера. */
const BEHIND_SECONDS = 20;
/** Сколько фрагментов тянуть параллельно. Каждый запрос идёт через воркер с
 *  проверкой прав, так что латентность заметна; последовательная качка делала
 *  старт втрое дольше, чем нужно. */
const PREFETCH = 3;
/** Запрос за фрагментом, который не ответил за это время, считаем повисшим и
 *  повторяем: телефон, ушедший из сети и вернувшийся, иначе оставит плеер
 *  ждать вечно. */
const FETCH_TIMEOUT_MS = 20_000;
/** Как часто сторож проверяет, что подкачка не встала. */
const WATCHDOG_MS = 1000;

export interface AttachOptions {
  video: HTMLVideoElement;
  index: VistaCaptureIndexRes;
  /** Диапазон байт из объекта. Обычно обёртка над fetch с токеном. */
  fetchRange: (offset: number, length: number) => Promise<ArrayBuffer>;
  onError?: (e: unknown) => void;
}

export interface AttachedPlayer {
  detach: () => void;
}

/**
 * Подключает MediaSource к <video>. Возвращает null, если браузер не тянет
 * MSE или кодек — вызывающий тогда падает на обычный src (запасной путь
 * обязателен: старый Safari и iOS до 17 умеют не всё).
 */
// На АЙФОНЕ обычного MediaSource нет вовсе — Apple открыла MSE на iPhone
// только через ManagedMediaSource (Safari 17.1+). Без этой ветки плеер на
// телефоне тихо падал в запасной путь и вычитывал весь файл: «на компе
// запускается почти сразу, на телефоне бесконечная прогрузка» (Tim, 12.08).
type MediaSourceCtor = {
  new (): MediaSource;
  isTypeSupported(type: string): boolean;
};
const MSCtor: MediaSourceCtor | undefined =
  (globalThis as unknown as { ManagedMediaSource?: MediaSourceCtor }).ManagedMediaSource ??
  (typeof MediaSource !== 'undefined' ? (MediaSource as unknown as MediaSourceCtor) : undefined);
const USING_MANAGED =
  !!(globalThis as unknown as { ManagedMediaSource?: unknown }).ManagedMediaSource;

export function attachFragmentedPlayer(opts: AttachOptions): AttachedPlayer | null {
  const { video, index, fetchRange } = opts;
  if (!MSCtor || !MSCtor.isTypeSupported(index.mime)) return null;
  if (!index.fragments.length) return null;

  const ms = new MSCtor();
  // ManagedMediaSource требует отключённого удалённого воспроизведения
  // (AirPlay): иначе Safari отказывается принимать источник.
  if (USING_MANAGED) video.disableRemotePlayback = true;
  const objectUrl = URL.createObjectURL(ms);
  let sb: SourceBuffer | null = null;
  let detached = false;
  // Какие фрагменты уже в буфере — чтобы не грузить дважды.
  const loaded = new Set<number>();
  // Начатые, но ещё не добавленные скачивания — чтобы параллельная подкачка не
  // запускала один и тот же фрагмент дважды.
  const inflight = new Map<number, Promise<ArrayBuffer>>();
  let pumping = false;
  let watchdog: ReturnType<typeof setInterval> | null = null;

  const fragEndMs = (i: number): number =>
    i + 1 < index.fragments.length ? index.fragments[i + 1]!.timeMs : index.durationMs;

  const waitIdle = (): Promise<void> =>
    new Promise((resolve) => {
      if (!sb || !sb.updating) return resolve();
      sb.addEventListener('updateend', () => resolve(), { once: true });
    });

  /** Скачать фрагмент с таймаутом и одним повтором. */
  async function fetchFragment(i: number): Promise<ArrayBuffer> {
    const f = index.fragments[i]!;
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        return await Promise.race([
          fetchRange(f.offset, f.size),
          new Promise<never>((_, reject) =>
            setTimeout(() => reject(new Error('fragment_timeout')), FETCH_TIMEOUT_MS),
          ),
        ]);
      } catch (e) {
        if (attempt === 1) throw e;
      }
    }
    throw new Error('unreachable');
  }

  /** Индексы фрагментов, нужных вокруг текущей позиции, по порядку. */
  function needed(): number[] {
    const t = video.currentTime * 1000;
    const from = t - 1000;
    const to = t + AHEAD_SECONDS * 1000;
    const out: number[] = [];
    for (let i = 0; i < index.fragments.length; i++) {
      if (loaded.has(i)) continue;
      const f = index.fragments[i]!;
      if (fragEndMs(i) < from || f.timeMs > to) continue;
      out.push(i);
    }
    return out;
  }

  /** Догрузить всё, что нужно вокруг текущей позиции. */
  async function pump(): Promise<void> {
    if (pumping || detached || !sb) return;
    pumping = true;
    try {
      for (;;) {
        if (detached || !sb) return;
        const want = needed();
        if (!want.length) break;

        // Тянем несколько кусков параллельно, добавляем строго по порядку:
        // appendBuffer последователен, а вот сеть ждать по одному незачем.
        for (const i of want.slice(0, PREFETCH)) {
          if (!inflight.has(i)) inflight.set(i, fetchFragment(i));
        }

        const next = want[0]!;
        const buf = await inflight.get(next)!;
        inflight.delete(next);
        if (detached || !sb) return;
        await waitIdle();
        if (detached || !sb) return;
        try {
          sb.appendBuffer(buf);
        } catch (e) {
          // QuotaExceededError — буфер полон. Чистим прошлое и пробуем снова
          // на следующем проходе, а не роняем воспроизведение.
          if ((e as DOMException)?.name === 'QuotaExceededError') {
            await evictBehind();
            continue;
          }
          throw e;
        }
        loaded.add(next);
        await waitIdle();
      }

      // Дошли до конца — закрываем поток, иначе плеер будет ждать продолжения
      // вечно и не покажет, что видео кончилось.
      const lastLoaded = loaded.has(index.fragments.length - 1);
      if (lastLoaded && ms.readyState === 'open') {
        try {
          ms.endOfStream();
        } catch {
          /* уже закрыт */
        }
      }
    } catch (e) {
      if (!detached) opts.onError?.(e);
    } finally {
      pumping = false;
    }
  }

  async function evictBehind(): Promise<void> {
    if (!sb) return;
    const keepFrom = Math.max(0, video.currentTime - BEHIND_SECONDS);
    if (keepFrom <= 0) return;
    await waitIdle();
    if (detached || !sb) return;
    try {
      sb.remove(0, keepFrom);
      await waitIdle();
      for (const i of [...loaded]) {
        if (fragEndMs(i) < keepFrom * 1000) loaded.delete(i);
      }
    } catch {
      /* remove на закрытом источнике — не наша беда */
    }
  }

  // Подкачку будят ЛЮБЫЕ признаки того, что плееру нужны данные. Раньше
  // здесь были только seeking и timeupdate — и это давало залипание: когда
  // воспроизведение упиралось в конец буфера, timeupdate переставал
  // приходить, а значит и подкачка больше не запускалась НИКОГДА. Ролик
  // «открылся, но встал» (Tim, 12.08) — ровно этот тупик.
  const wake = () => {
    void pump();
  };
  const WAKE_EVENTS = ['seeking', 'seeked', 'timeupdate', 'waiting', 'stalled', 'canplay', 'play'];

  ms.addEventListener('sourceopen', () => {
    if (detached) return;
    try {
      sb = ms.addSourceBuffer(index.mime);
      // 'segments': времена берутся из самих фрагментов, а не выстраиваются
      // подряд — иначе перемотка сложила бы куски в неверном порядке.
      sb.mode = 'segments';
      if (index.durationMs > 0) ms.duration = index.durationMs / 1000;
    } catch (e) {
      opts.onError?.(e);
      return;
    }
    void (async () => {
      try {
        const init = await fetchRange(0, index.initSize);
        if (detached || !sb) return;
        sb.appendBuffer(init);
        await waitIdle();
        await pump();
      } catch (e) {
        if (!detached) opts.onError?.(e);
      }
    })();
  });

  for (const ev of WAKE_EVENTS) video.addEventListener(ev, wake);
  // ManagedMediaSource сама говорит, когда ей нужны данные, и когда буфера
  // достаточно. Это не замена нашей подкачке, а дополнительный будильник:
  // на телефоне экономия трафика решается браузером, и игнорировать его
  // сигналы — значит грузить лишнее или не грузить вовсе.
  if (USING_MANAGED) {
    ms.addEventListener('startstreaming', wake);
  }
  // Сторож — последняя линия обороны: даже если ни одно событие не пришло
  // (а браузеры на этот счёт непоследовательны), раз в секунду проверяем сами.
  watchdog = setInterval(wake, WATCHDOG_MS);
  // srcObject предпочтительнее для ManagedMediaSource (так это описано у
  // Apple); там, где его нет, остаётся обычный objectURL.
  try {
    if (USING_MANAGED && 'srcObject' in video) {
      (video as unknown as { srcObject: unknown }).srcObject = ms;
    } else {
      video.src = objectUrl;
    }
  } catch {
    video.src = objectUrl;
  }

  return {
    detach: () => {
      detached = true;
      if (watchdog !== null) clearInterval(watchdog);
      watchdog = null;
      inflight.clear();
      for (const ev of WAKE_EVENTS) video.removeEventListener(ev, wake);
      try {
        if (ms.readyState === 'open') ms.endOfStream();
      } catch {
        /* уже закрыт */
      }
      URL.revokeObjectURL(objectUrl);
      try {
        (video as unknown as { srcObject: unknown }).srcObject = null;
      } catch {
        /* не поддерживается — достаточно снять src */
      }
      video.removeAttribute('src');
      video.load();
    },
  };
}
